import React from 'react';
import PropTypes from 'prop-types';
import {
    space, colors, fontSizes, mediaQueries, fontWeights, lineHeights, forms
} from 'style/config';
import basketConstants from 'constants/Basket';
import { wrapFunctionalComponent } from 'utils/framework';
import { getImageAltText } from 'utils/Accessibility';
import StringUtils from 'utils/String';
import {
    Grid, Box, Text, Button, Divider
} from 'components/ui';
import ProductLove from 'components/Product/ProductLove';
import ProductLoveToggle from 'components/Product/ProductLove/ProductLoveToggle';
import ProductImage from 'components/Product/ProductImage';
import useAutoReplenishmentCancelledItem from 'components/RichProfile/MyAccount/AutoReplenishment/AutoReplenishmentCancelledItem/hooks/useAutoReplenishmentCancelledItem';

const { DELIVERY_OPTIONS } = basketConstants;
const sourceParam = DELIVERY_OPTIONS.AUTO_REPLENISH.toLowerCase();

/**
 * Displays a single cancelled auto-replenishment subscription with product details,
 * pricing, cancellation date, and options to view the product or re-love it.
 *
 * @param {Object} props
 * @param {Object} props.subscription - Cancelled subscription data
 */
function AutoReplenishmentCancelledItem({ subscription }) {
    const { localization, product, hasItemVariation, subscriptionEndDate } = useAutoReplenishmentCancelledItem({ subscription });

    return (
        <Box css={styles.itemContainer}>
            <Divider marginY={0} />
            <Grid css={styles.gridContainer}>
                <Box>
                    <ProductImage
                        id={product.skuId}
                        size={97}
                        skuImages={product.skuImages}
                        disableLazyLoad={true}
                        altText={getImageAltText(product)}
                    />
                </Box>

                <Box>
                    <Text
                        is='p'
                        css={styles.title}
                        children={product.brandName}
                    />
                    <Text
                        is='p'
                        css={styles.subtitle}
                        children={product.productName}
                    />
                    {hasItemVariation && (
                        <Box css={styles.detailsContainer}>
                            <Text
                                is='p'
                                css={styles.detailsText}
                            >
                                {`${product.variationType}: ${product.variationValue}`}
                            </Text>
                        </Box>
                    )}
                    {!!subscriptionEndDate && (
                        <Text
                            is='p'
                            css={styles.cancelDateText}
                        >
                            {StringUtils.format(localization.cancelledOn, subscriptionEndDate)}
                        </Text>
                    )}
                </Box>
                <Box css={styles.priceContainer}>
                    <div>
                        <Text
                            is='del'
                            css={styles.listPrice}
                        >
                            {product.productPrice}
                        </Text>
                        <Text
                            is='p'
                            css={styles.salePrice}
                        >
                            {product.discountedPrice}
                        </Text>
                        <Text
                            is='p'
                            css={styles.salePrice}
                        >
                            {StringUtils.format(localization.discountRate, product.discountRate)}
                        </Text>
                    </div>
                </Box>
                <Box css={styles.ctaContainer}>
                    <Button
                        size='sm'
                        variant='secondary'
                        css={styles.secondaryCta}
                        href={`/product/${product.productId}?skuId=${product.skuId}&source=${sourceParam}`}
                    >
                        {localization.viewProductDetails}
                    </Button>
                </Box>
                <Box css={styles.productLoveContainer}>
                    <ProductLove
                        sku={product}
                        loveSource='productPage'
                        productId={product.productId}
                        imageProps={{
                            id: product.skuId,
                            skuImages: product.skuImages,
                            disableLazyLoad: true,
                            altText: getImageAltText(product)
                        }}
                    >
                        <ProductLoveToggle
                            productId={product.productId}
                            size={20}
                            width={30}
                            height={30}
                        />
                    </ProductLove>
                </Box>
            </Grid>
        </Box>
    );
}

const styles = {
    itemContainer: {
        paddingTop: space[3],
        paddingRight: space[4]
    },
    gridContainer: {
        alignItems: 'start',
        gap: space[1],
        gridTemplateColumns: `min(97px) auto max(${space[8]}px)`,
        lineHeight: lineHeights.tight,
        padding: `${space[4]}px 0`,
        [mediaQueries.md]: {
            gridTemplateColumns: `auto min(240px) max(${space[8]}px) 1fr max(${space[6]}px)`,
            gap: space[4]
        }
    },
    title: {
        fontSize: fontSizes.base,
        fontWeight: fontWeights.bold,
        marginBottom: space[1]
    },
    subtitle: {
        fontSize: fontSizes.base,
        lineHeight: lineHeights.tight,
        marginBottom: space[1]
    },
    detailsContainer: {
        color: colors.gray,
        fontSize: fontSizes.sm,
        marginBottom: space[1]
    },
    detailsText: {
        display: 'inline-block',
        lineHeight: lineHeights.tight
    },
    priceContainer: {
        textAlign: 'right'
    },
    salePrice: {
        color: colors.red,
        fontWeight: fontWeights.bold
    },
    listPrice: {
        color: colors.black,
        fontWeight: fontWeights.bold
    },
    cancelDateText: {
        fontSize: fontSizes.sm,
        lineHeight: lineHeights.tight
    },
    ctaContainer: {
        gridColumn: 2,
        marginTop: space[2],
        [mediaQueries.md]: { gridColumn: 'auto', textAlign: 'right' }
    },
    secondaryCta: {
        borderWidth: forms.BORDER_WIDTH,
        fontWeight: fontWeights.normal
    },
    productLoveContainer: {
        textAlign: 'center',
        marginTop: space[2],
        paddingLeft: space[4],
        [mediaQueries.md]: { paddingLeft: 0 }
    }
};

AutoReplenishmentCancelledItem.propTypes = {
    subscription: PropTypes.shape({
        subscriptionEndDate: PropTypes.string,
        items: PropTypes.arrayOf(
            PropTypes.shape({
                brandName: PropTypes.string,
                discountAmount: PropTypes.string,
                discountedPrice: PropTypes.string,
                price: PropTypes.string,
                productId: PropTypes.string,
                productName: PropTypes.string,
                qty: PropTypes.number,
                skuId: PropTypes.string,
                skuImages: PropTypes.object,
                variationType: PropTypes.string,
                variationValue: PropTypes.string
            })
        )
    }).isRequired
};

export default wrapFunctionalComponent(AutoReplenishmentCancelledItem, 'AutoReplenishmentCancelledItem');
